import { http } from '@noor/api';
import { env } from '@noor/config';
import {
  productBannerResponseSchema,
  productBannerSchema,
  type CreateProductBannerInput,
  type ProductBanner,
  type UpdateProductBannerInput,
} from '../model/product-banner';
import { BANNER_PLACEMENT } from '../config/banner';
import type { BusinessListFilter } from '../model/list-params';
import {
  fetchAllBusinessPages,
  normalizeBusinessList,
  unwrapDataPayload,
  type BusinessListResult,
} from './envelope';

const eatsBase = { baseURL: env.VITE_EATS_API_URL };

export interface ProductBannerWriteResult {
  banner: ProductBanner | null;
  gated: boolean;
}

function unwrapWrite(raw: unknown): ProductBannerWriteResult {
  const parsed = productBannerResponseSchema.parse(unwrapDataPayload(raw));
  return { banner: parsed.banner ?? null, gated: parsed.pending_change != null };
}

export const productBannerApi = {
  list(filter: BusinessListFilter): Promise<BusinessListResult<ProductBanner>> {
    return http
      .get<unknown>('/api/v1/admin/banners', {
        ...eatsBase,
        query: {
          'pagination.limit': String(filter.limit),
          'pagination.offset': String(filter.offset),
          placement: BANNER_PLACEMENT.restaurantPage,
        },
      })
      .then((raw) => normalizeBusinessList(raw, productBannerSchema, filter));
  },

  listAll(): Promise<ProductBanner[]> {
    return fetchAllBusinessPages((filter) => productBannerApi.list(filter));
  },

  async create(input: CreateProductBannerInput): Promise<ProductBannerWriteResult> {
    const raw = await http.post<unknown>('/api/v1/admin/banners', input, eatsBase);
    return unwrapWrite(raw);
  },

  async update(
    id: string,
    input: UpdateProductBannerInput,
  ): Promise<ProductBannerWriteResult> {
    const raw = await http.patch<unknown>(`/api/v1/admin/banners/${id}`, input, eatsBase);
    return unwrapWrite(raw);
  },

  async remove(id: string): Promise<void> {
    await http.delete<unknown>(`/api/v1/admin/banners/${id}`, eatsBase);
  },
};
